/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core import
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as PapaParse from 'papaparse';

// Component imports
import { Button, Col, Modal, Row, Typography, Upload } from 'antd';

// Misc imports
import handleFileDownload from '../../utils/misc/handleFileDownload';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useParams } from 'react-router-dom';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const CSV_PARSER_OPTIONS = {
  header: true,
  dynamicTyping: true,
  skipEmptyLines: true,
  transformHeader: header => header.toLowerCase().replace(/\W/g, '_'),
  transform: (value, header) => value
};

const TestSetsImportModal = ({
  cases = [],
  onClose = () => {},
  isModalOpen,
  handleImport=() => {},
  handleRestore=() => {},
  isCreateMode = false
}) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();

  const { isFetching: isFetchingAutomatedAnswers } = getCurrentQueryState('fetchAutomatedAnswers', { orgId, tenantId });
  const onDownload = async () => {
    const testCases = [];
    cases.forEach(i => {
      if (i.input.trim() !== '' && i.intent.trim() !== '') {
        testCases.push(i);
      }
    });
    var csv = PapaParse.unparse(
      (testCases || [])
        .map(a => {
          return {
            'User Input': a.input,
            'Expected Automated Answer': a.intent
          };
        })
    );

    var csvData = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const fileName = 'test_cases.csv';
    handleFileDownload(csvData, fileName);
    onClose();
  };

  const handleUpload = (file, handler) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const csvData = PapaParse.parse(
        event.target.result,
        Object.assign(CSV_PARSER_OPTIONS, {
          error: () => {},
          encoding: 'UTF-8'
        })
      );

      const filteredCsvData = {
        ...csvData,
        data: csvData?.data?.filter(row => row.user_input && row.expected_automated_answer)
      };

      // Convert data to our schema
      const importedTestSetCases = [...filteredCsvData.data];
      const payload = importedTestSetCases.map(item => ({
        input: item.user_input,
        intent: item.expected_automated_answer
      }));
      if (payload.length) {
        handler(payload);
      } else {
        openConsoleAlertMessage({ type: 'error', message: t('No test cases related data to process') });
      }
    };

    reader.readAsText(file);
    return false;
  };

  return (
    <Modal
      title={isCreateMode ? t('Import Test Cases') : t('Import / Export Test Cases')}
      open={isModalOpen}
      onCancel={onClose}
      style={{ maxWidth: 600 }}
      width={'95%'}
      centered
      footer={null}
    >
      <Row gutter={[24, 18]}>
        <Col xs={24} sm={10} hidden={isCreateMode}>
          <Button type="primary" size="large" block onClick={onDownload} loading={isFetchingAutomatedAnswers}>
            {t('Export as CSV')}
          </Button>
        </Col>

        <Col xs={24} sm={14} className="align-self-center" hidden={isCreateMode}>
          <Typography.Text type="secondary">
            {t('Create a backup of the test set')}
          </Typography.Text>
        </Col>

        <Col xs={24} sm={10}>
          <Upload
            accept=".csv"
            className="ant-upload-w-100"
            showUploadList={false}
            beforeUpload={file => {
              handleUpload(file, (payload) => handleRestore(payload));
            }}
          >
            <Button type="primary" size="large" block loading={isFetchingAutomatedAnswers}>
              {t('Restore from CSV')}
            </Button>
          </Upload>
        </Col>

        <Col xs={24} sm={14} className="align-self-center">
          <Typography.Text type="secondary">
            {t('Replace the current set of test cases with a new one. All the cases in the older version will be lost after save.')}
          </Typography.Text>
        </Col>

        <Col xs={24} sm={10}>
          <Upload
            accept=".csv"
            className="ant-upload-w-100"
            showUploadList={false}
            beforeUpload={file => {
              handleUpload(file, (payload) => handleImport(payload));
            }}
          >
            <Button type="primary" size="large" block loading={isFetchingAutomatedAnswers}>
              {t('Import from CSV')}
            </Button>
          </Upload>
        </Col>

        <Col xs={24} sm={14} className="align-self-center">
          <Typography.Text type="secondary">
            {t('Upload new cases without deleting the current ones.')}
          </Typography.Text>
        </Col>

        <Col xs={24} sm={10}>
          <Button type="primary" size="large" block>
            <a
              href={`${window.location.origin.toString()}/nlp_test_run_sample.xlsx`}
              download
              target="_blank"
              rel="noreferrer"
            >
              {t('Download Sample XLSX')}
            </a>
          </Button>
        </Col>

        <Col xs={24} sm={14} className="align-self-center">
          <Typography.Text type="secondary">
            {t('Download a sample file with instructions for importing test cases.')}
          </Typography.Text>
        </Col>
      </Row>
    </Modal>
  );
};

export default TestSetsImportModal;

TestSetsImportModal.propTypes = {
  tempTestCases: PropTypes.array,
  onClose: PropTypes.func,
  isModalOpen: PropTypes.bool,
  handleImport: PropTypes.func,
  handleRestore: PropTypes.func,
  isCreateMode: PropTypes.bool,
  cases: PropTypes.array
};
